import { Box, Button, Text, VStack } from '@chakra-ui/react'
import { sameChoice, stepText, type Choice, type ConvStep } from '../../../../lib/conversation'
import type { ScriptLanguage } from '../../../../lib/walkPrefs'

const EYEBROW = { say: 'Say', ask: 'Ask', decide: 'Decide' } as const

/** [Spec §4.6] The current step: highlighted, larger text, its answers pinned low (≥52px). */
export function StepCard({ step, language, selected, onChoose, notice, onCancel }: {
  step: ConvStep; language: ScriptLanguage; selected: Choice | null; onChoose: (c: Choice) => void
  notice: string | null; onCancel?: () => void
}) {
  const el = step.elements[0]
  const options: { label: string; choice: Choice }[] = step.kind === 'ask'
    ? [...(el.answers ?? [])].sort((a, b) => a.displayOrder - b.displayOrder)
        .map(a => ({ label: (language === 'es' && a.textSpanish) || a.text, choice: { stepId: step.id, kind: 'answer', answerId: a.id } }))
    : step.kind === 'decide'
      ? (el.promptResponses ?? []).map((p, index) => ({ label: p.label, choice: { stepId: step.id, kind: 'response', index } }))
      : []
  return (
    <VStack align="stretch" gap={4} p={4} borderWidth="2px" borderColor="accent" borderRadius="lg" bg="surface" aria-current="step">
      <Box>
        <Text fontSize="xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="accent">{EYEBROW[step.kind]}</Text>
        {step.kind === 'say'
          ? step.elements.map(t => (
              <Text key={t.id} fontSize="lg" whiteSpace="pre-wrap" mt={2}>{(language === 'es' && t.textContentSpanish) || t.textContent}</Text>
            ))
          : <Text fontSize="xl" fontWeight="semibold" mt={1}>{stepText(step, language)}</Text>}
      </Box>
      {notice && <Box p={3} borderRadius="md" bg="warning.subtle" color="warning.fg">{notice}</Box>}
      {step.kind === 'say' ? (
        <Button minH="52px" bg="accent" color="accent.fg" onClick={() => onChoose({ stepId: step.id, kind: 'read' })}>Next</Button>
      ) : (
        <VStack align="stretch" gap={2}>
          {options.map(o => {
            const on = sameChoice(selected ?? undefined, o.choice)
            return (
              <Button key={o.choice.kind === 'answer' ? o.choice.answerId : `r${(o.choice as { index: number }).index}`}
                minH="52px" whiteSpace="normal" justifyContent="flex-start" textAlign="left" px={4} variant="outline"
                borderWidth="2px" borderColor={on ? 'accent' : 'border'} bg={on ? 'accent.soft' : 'surface'} color="fg"
                aria-pressed={on} onClick={() => onChoose(o.choice)}>
                {o.label}
              </Button>
            )
          })}
        </VStack>
      )}
      {onCancel && <Button variant="ghost" minH="44px" onClick={onCancel}>Cancel</Button>}
    </VStack>
  )
}
